import { useCallback, useEffect, useRef, useState } from "react"

export const MAX_LINEAS_LOG = 2000
export const INTERVALO_VOLCADO_MS = 250

/**
 * BDSJEA-345: acumula líneas de log fuera del estado de React y las vuelca a
 * `logs` como mucho cada `intervaloMs`, para no re-renderizar por cada línea
 * (el migrador emite miles). Conserva solo las últimas `max` líneas y cuenta
 * el total recibido para poder avisar que se recortó.
 *
 * @returns {{ logs: string[], total: number, agregar: function,
 *   volcar: function, limpiar: function }}
 */
const useLogsAcumulados = ({
  max = MAX_LINEAS_LOG,
  intervaloMs = INTERVALO_VOLCADO_MS,
} = {}) => {
  const [vista, setVista] = useState({ logs: [], total: 0 })
  const bufferRef = useRef([])
  const totalRef = useRef(0)
  const timerRef = useRef(null)

  const cancelarTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  // El buffer puede crecer hasta 2 * max (se recorta por tandas, no por línea);
  // lo que se publica son siempre las últimas `max`.
  const publicar = useCallback(() => {
    if (bufferRef.current.length > max) {
      bufferRef.current = bufferRef.current.slice(-max)
    }
    setVista({ logs: bufferRef.current.slice(), total: totalRef.current })
  }, [max])

  const volcar = useCallback(() => {
    cancelarTimer()
    publicar()
  }, [cancelarTimer, publicar])

  const agregar = useCallback(
    linea => {
      bufferRef.current.push(linea)
      totalRef.current += 1
      if (bufferRef.current.length >= max * 2) {
        bufferRef.current = bufferRef.current.slice(-max)
      }
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null
          publicar()
        }, intervaloMs)
      }
    },
    [max, intervaloMs, publicar],
  )

  const limpiar = useCallback(() => {
    cancelarTimer()
    bufferRef.current = []
    totalRef.current = 0
    setVista({ logs: [], total: 0 })
  }, [cancelarTimer])

  // Al desmontar no queda ningún timer pendiente.
  useEffect(() => cancelarTimer, [cancelarTimer])

  return { logs: vista.logs, total: vista.total, agregar, volcar, limpiar }
}

export default useLogsAcumulados
